import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as pendingTransfersInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_projectApprovalTableSearchAndFilterBar.module.scss';
import { TPendingTransfersApprovalListingFilters } from '~types/feat/approval';
import { MetalTypeEnum } from '~utils/enums';

const TransfersApprovalSearchAndFilterBar = () => {
    const [sourceBranchSearch, setSourceBranchSearch] = useState('');
    const [targetBranchSearch, setTargetBranchSearch] = useState('');

    const filters = useAppSelector(state => state.approval.pendingTransferListing.filters);
    const branchesList = useAppSelector((state) => state.branch.allBranches);

    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const initialFilters = pendingTransfersInitialState.pendingTransferListing.filters;

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TPendingTransfersApprovalListingFilters) => dispatch(combinedActions.approvalSetpendingTransferListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingTransferListingAttempt());

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            const userBranch = branchesList.find(branch => branch.branchCode === branchCode);

            if (userBranch) {
                setFilters({
                    ...filters,
                    branchFrom: userBranch.branchId,
                    branchTo: userBranch.branchId,
                });
            }
        }
    }, [branchesList]);

    const handleCancelClick = () => {
        setSourceBranchSearch('');
        setTargetBranchSearch('');

        const userBranch = branchesList.find(branch => branch.branchCode === branchCode);

        setFilters({
            ...initialFilters,
            branchFrom: !allBranchAccess ? userBranch?.branchId || 0 : 0,
            branchTo: !allBranchAccess ? userBranch?.branchId || 0 : 0,
        });

        handleSearchClick();
    };

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];
        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Source Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchFrom ? branchesList.find(branch => branch.branchId === filters.branchFrom)?.branchName || '' : sourceBranchSearch,
                setValue: event => {
                    setSourceBranchSearch(event.target.value);

                    const selectedBranch = branchesList.find(
                        branch => branch.branchName === event.target.value
                    );

                    setFilters({
                        ...filters,
                        branchFrom: selectedBranch?.branchId || 0,
                    });
                },
                optionSelected: !!filters.branchFrom,
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Target Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchTo ? branchesList.find(branch => branch.branchId === filters.branchTo)?.branchName || '' : targetBranchSearch,
                setValue: event => {
                    setTargetBranchSearch(event.target.value);

                    const selectedBranch = branchesList.find(
                        branch => branch.branchName === event.target.value
                    );

                    setFilters({
                        ...filters,
                        branchTo: selectedBranch?.branchId || 0,
                    });
                },
                optionSelected: !!filters.branchTo,
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                maxDate: dayjs().toISOString(),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
            gridColumns={2}
        />
    );
};

export default TransfersApprovalSearchAndFilterBar;
